/**
 * EventQueue — a durable, batched event buffer. No events lost.
 *
 * Durability model:
 *   - Every enqueue write-throughs the whole buffer to @lib/storage
 *     (AsyncStorage-backed), so a crash/kill before flush recovers the rows on
 *     next launch (see _hydrate()).
 *   - A batch is handed to flushFn but the rows are cleared from the buffer ONLY
 *     after flushFn resolves (HTTP 202). If the POST rejects, the rows stay
 *     persisted and are retried on the next window — a transient network failure
 *     never drops events.
 *   - An `inflight` guard prevents the same prefix being sent twice concurrently.
 *   - MAX_QUEUE bounds storage if the endpoint is unreachable for a long time
 *     (drops oldest, logs how many).
 *
 * tl-app's @lib/storage is value-in/value-out (getData/setData handle their own
 * JSON), so we pass/read the array directly — no manual JSON here.
 *
 * Flush triggers: MAX_BATCH events | INTERVAL ms | explicit flush() (AppState
 * background). flushFn(batch) must return a promise that rejects on failure.
 */
import { getData, setData } from "@lib/storage";
import type { OaUserEvent } from "./types";

type FlushFn = (batch: OaUserEvent[]) => Promise<unknown>;

const MAX_BATCH = 20;
const INTERVAL = 10_000;
const MAX_QUEUE = 1000;
const STORE_KEY = "wisetap_event_queue";

export class EventQueue {
  private buf: OaUserEvent[] = [];
  private readonly flushFn: FlushFn;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inflight = false;

  constructor(flushFn: FlushFn) {
    this.flushFn = flushFn;
    this._hydrate();
  }

  private async _hydrate(): Promise<void> {
    try {
      const saved = await getData(STORE_KEY);
      if (Array.isArray(saved) && saved.length) {
        // recovered rows are older → front of the queue, then anything queued since
        this.buf = saved.concat(this.buf);
        this._persist();
      }
    } catch {
      // corrupt/absent persisted queue — start clean
    }
    if (this.buf.length) this._schedule();
  }

  private _persist(): void {
    setData(STORE_KEY, this.buf);
  }

  private _schedule(): void {
    if (!this.timer) this.timer = setTimeout(() => this.flush(), INTERVAL);
  }

  enqueue(row: OaUserEvent): void {
    this.buf.push(row);
    if (this.buf.length > MAX_QUEUE) {
      const dropped = this.buf.length - MAX_QUEUE;
      this.buf.splice(0, dropped);
      // eslint-disable-next-line no-console
      console.log(`wisetap queue overflow, dropped ${dropped} oldest events`);
    }
    this._persist();
    if (this.buf.length >= MAX_BATCH) {
      this.flush();
    } else {
      this._schedule();
    }
  }

  flush(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.inflight || !this.buf.length) return;
    this.inflight = true;
    const batch = this.buf.slice(0, MAX_BATCH); // bounded chunk; the sent prefix
    Promise.resolve()
      .then(() => this.flushFn(batch))
      .then(() => {
        // acked — drop only the sent prefix, keep anything queued meanwhile
        this.buf = this.buf.slice(batch.length);
        this._persist();
        this.inflight = false;
        if (this.buf.length) this._schedule();
      })
      .catch(() => {
        // delivery failed — rows remain persisted; retry next window
        this.inflight = false;
        this._schedule();
      });
  }
}

export default EventQueue;
